import React, { useState } from 'react';
import { View } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { currentPeriod, fmtDate, P } from '@hrms/shared';
import { useAuth } from '../../../auth/AuthProvider';
import { api } from '../../../lib/api';
import { eachDay } from '../../../lib/date';
import { Avatar, CalChip, CalendarMonth, Card, Dot, ListItem, Loading, Modal, PageHeader, Row, Screen, Segmented, StatusBadge, Text } from '../../../ui';

type Entry = { name: string; code: string; color: string; status: string; designation?: string | null };

/** Who in my team is on leave, month by month. */
export function TeamLeaveCalendarScreen() {
  const { can } = useAuth();
  const allowAll = can(P.LEAVE_MANAGE, P.LEAVE_VIEW);
  const [month, setMonth] = useState(currentPeriod());
  const [scope, setScope] = useState<'team' | 'all'>('team');
  const [sel, setSel] = useState<string | null>(null);
  const q = useQuery({ queryKey: ['leave', 'team-calendar', month, scope], queryFn: () => api.leave.teamCalendar(month, scope) });

  const byDate = new Map<string, Entry[]>();
  const types = new Map<string, { name: string; color: string }>();
  for (const e of q.data ?? [])
    for (const l of e.leaves) {
      types.set(l.leave_type.code, { name: l.leave_type.name, color: l.leave_type.color });
      for (const d of eachDay(l.from_date, l.to_date)) {
        if (!d.startsWith(month)) continue;
        const arr = byDate.get(d) ?? [];
        arr.push({ name: e.employee.full_name, code: l.leave_type.code, color: l.leave_type.color, status: l.status, designation: e.employee.designation });
        byDate.set(d, arr);
      }
    }

  return (
    <Screen>
      <PageHeader title="Team leave calendar" subtitle="See who is on leave." />
      <Card>
        {q.isLoading ? (
          <Loading />
        ) : (
          <CalendarMonth
            month={month}
            onMonthChange={setMonth}
            onDayPress={(d) => byDate.get(d)?.length && setSel(d)}
            headerExtra={allowAll ? <Segmented value={scope} onChange={setScope} options={[{ key: 'team', label: 'My team' }, { key: 'all', label: 'Everyone' }]} /> : undefined}
            renderDay={(d) => {
              const list = byDate.get(d) ?? [];
              return (
                <>
                  {list.slice(0, 3).map((x, i) => (
                    <CalChip key={i} color={x.color} faded={x.status === 'pending'}>
                      {x.name.split(' ')[0]} · {x.code}
                    </CalChip>
                  ))}
                  {list.length > 3 && <Text size={10} variant="small">+{list.length - 3} more</Text>}
                </>
              );
            }}
          />
        )}
        <Row gap={12} style={{ marginTop: 12 }}>
          {[...types.values()].map((t) => (
            <Row key={t.name} gap={4}>
              <Dot color={t.color} />
              <Text variant="small">{t.name}</Text>
            </Row>
          ))}
          <Text variant="small">Faded = pending approval</Text>
        </Row>
      </Card>
      <Modal open={!!sel} onClose={() => setSel(null)} title={sel ? `On leave · ${fmtDate(sel)}` : ''} width={420} noScroll>
        <View>
          {(sel ? byDate.get(sel) ?? [] : []).map((x, i) => (
            <ListItem key={i} left={<Avatar name={x.name} />} title={x.name} subtitle={x.designation} right={<Row gap={6}><Dot color={x.color} /><Text>{x.code}</Text><StatusBadge status={x.status} /></Row>} />
          ))}
        </View>
      </Modal>
    </Screen>
  );
}
